/**
 * assets/product-form.js — Studio 1/1
 * ---------------------------------------------------------------------------
 * Phase 2C: JavaScript behaviour for the product form (variant selection,
 * quantity stepper, async add-to-cart).
 *
 * Responsibilities:
 *   • Read product variants from embedded JSON ([data-product-json])
 *   • Respond to option radio changes → find matching variant
 *   • Update: price display, ATC button state (available / sold-out),
 *     hidden variant input, SKU
 *   • Quantity stepper (+ / − buttons with floor at 1)
 *   • Submit form via POST /cart/add.js (AJAX)
 *   • On success: call window.Studio1of1.cart.refresh() + .open()
 *   • On error: show inline error, restore button
 *   • Dispatch studio1of1:cart:item-added for future listeners
 *
 * Consumed data-* hooks (set by snippets/product-form.liquid):
 *   [data-product-form]       Form root
 *   [data-product-id]         Product ID (data attr on form)
 *   [data-variant-input]      Hidden <input name="id"> — variant ID
 *   [data-option-selector]    Wrapper per option group
 *   [data-variant-option]     Individual radio <input>
 *   [data-qty-decrease]       Decrement quantity button
 *   [data-qty-increase]       Increment quantity button
 *   [data-qty-input]          Quantity number <input>
 *   [data-atc-btn]            Submit button
 *   [data-atc-text]           Text span inside submit button
 *   [data-price-wrapper]      Price <div> (receives price--on-sale class)
 *   [data-price-current]      Current price <dd>
 *   [data-price-compare]      Compare-at price <dd>
 *   [data-form-error]         Error message container
 *   [data-product-json]       <script> with serialised product JSON
 *
 * Consumed from sections/main-product.liquid:
 *   [data-gallery]            Gallery root
 *   [data-gallery-slide]      Individual slide wrappers
 *   [data-gallery-thumb]      Thumbnail buttons
 *   [data-gallery-main]       Main image container
 *
 * Public API:
 *   (none — behaviour is self-contained)
 *
 * Dependencies (must be loaded first):
 *   assets/cart.js  — window.Studio1of1.cart.{ open, refresh }
 *
 * Constraints (Phase 2C governance):
 *   • IIFE, 'use strict'
 *   • No inline event handlers
 *   • XSS: all product data rendered via Liquid (trusted); money formatted
 *     server-side or via Intl.NumberFormat matching cart.js convention
 * ---------------------------------------------------------------------------
 */

'use strict';

(() => {

  // ── Currency formatting ────────────────────────────────────────────────
  const shop     = window.Studio1of1?.shop || {};
  const currency = shop.currency || 'USD';

  function formatMoney(cents) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      minimumFractionDigits: 2,
    }).format(cents / 100);
  }

  // ── Per-form initialisation ────────────────────────────────────────────
  // Support multiple product forms on one page (e.g. featured + quick-add)
  document.querySelectorAll('[data-product-form]').forEach(initForm);

  function initForm(form) {

    // ── Read product JSON ─────────────────────────────────────────────
    const jsonEl = document.querySelector('[data-product-json]');
    if (!jsonEl) return;

    let productData;
    try {
      productData = JSON.parse(jsonEl.textContent);
    } catch (e) {
      console.error('[product-form.js] Failed to parse product JSON', e);
      return;
    }

    const variants = productData.variants || [];

    // ── Element refs ──────────────────────────────────────────────────
    const variantInput  = form.querySelector('[data-variant-input]');
    const atcBtn        = form.querySelector('[data-atc-btn]');
    const atcText       = form.querySelector('[data-atc-text]');
    const spinner       = form.querySelector('.product-form__spinner');
    const errorEl       = form.querySelector('[data-form-error]');
    const qtyInput      = form.querySelector('[data-qty-input]');
    const qtyDecreaseEl = form.querySelector('[data-qty-decrease]');
    const qtyIncreaseEl = form.querySelector('[data-qty-increase]');

    // Price elements — may be outside the form (in product-info section)
    const priceWrapper  = document.querySelector('[data-price-wrapper]');
    const priceCurrent  = document.querySelector('[data-price-current]');
    const priceCompare  = document.querySelector('[data-price-compare]');
    const skuEl         = document.querySelector('[data-variant-sku]');

    // ── State ──────────────────────────────────────────────────────────
    let currentVariant = findVariantFromSelections() || variants[0];

    // ── Helpers ────────────────────────────────────────────────────────

    /**
     * Read the current radio selections and return the matching variant (or null).
     */
    function findVariantFromSelections() {
      const selectors = form.querySelectorAll('[data-option-selector]');
      if (!selectors.length) {
        // Single-variant product
        return variants[0] || null;
      }

      const selected = Array.from(selectors).map(sel => {
        const checked = sel.querySelector('[data-variant-option]:checked');
        return checked ? checked.dataset.optionValue : null;
      });

      return variants.find(v => {
        const opts = [v.option1, v.option2, v.option3].slice(0, selected.length);
        return opts.every((val, i) => val === selected[i]);
      }) || null;
    }

    /**
     * Update price display for a given variant.
     */
    function updatePrice(variant) {
      if (!variant || !priceCurrent) return;

      const onSale = variant.compare_at_price > variant.price;

      priceCurrent.textContent = formatMoney(variant.price);

      if (priceWrapper) {
        priceWrapper.classList.toggle('price--on-sale', onSale);
      }

      if (priceCompare) {
        const compareRow = priceCompare.closest('.price__item--compare');
        if (compareRow) compareRow.hidden = !onSale;
        if (onSale) priceCompare.querySelector('s').textContent = formatMoney(variant.compare_at_price);
      }
    }

    /**
     * Update ATC button state for a given variant.
     */
    function updateButton(variant) {
      if (!atcBtn || !atcText) return;

      if (!variant || !variant.available) {
        atcBtn.disabled = true;
        atcBtn.setAttribute('aria-disabled', 'true');
        atcText.textContent = variant
          ? (window.Studio1of1?.i18n?.sold_out || 'Sold out')
          : (window.Studio1of1?.i18n?.unavailable || 'Unavailable');
      } else {
        atcBtn.disabled = false;
        atcBtn.removeAttribute('aria-disabled');
        var label = window.Studio1of1?.i18n?.add_to_cart || 'Add to cart';
        atcText.textContent = label + ' — ' + formatMoney(variant.price);
      }
    }

    /**
     * Update hidden variant input + SKU display.
     */
    function updateMeta(variant) {
      if (variantInput && variant) variantInput.value = variant.id;
      if (skuEl) skuEl.textContent = variant?.sku || '';
    }

    /**
     * Show or hide loading state on ATC button.
     */
    function setLoading(loading) {
      if (!atcBtn) return;
      atcBtn.disabled = loading || !currentVariant?.available;
      if (atcText)  atcText.hidden  = loading;
      if (spinner)  spinner.hidden  = !loading;
    }

    /**
     * Show inline error message.
     */
    function showError(msg) {
      if (!errorEl) return;
      errorEl.textContent = msg;
      errorEl.hidden = false;
    }

    /**
     * Hide inline error.
     */
    function clearError() {
      if (!errorEl) return;
      errorEl.textContent = '';
      errorEl.hidden = true;
    }

    /**
     * Apply all state updates for a newly resolved variant.
     */
    function applyVariant(variant) {
      currentVariant = variant;
      updateMeta(variant);
      updatePrice(variant);
      updateButton(variant);
    }

    // ── Gallery sync ───────────────────────────────────────────────────
    // When variant changes, scroll gallery to the variant's featured_image.
    // (Media IDs come from product JSON; gallery slides have data-media-id.)
    function syncGalleryToVariant(variant) {
      if (!variant) return;
      const mediaId = variant.featured_image && variant.featured_image.id;
      if (!mediaId) return;

      const gallery = document.querySelector('[data-gallery]');
      if (!gallery) return;

      const mediaIdStr = String(mediaId);
      const slides = gallery.querySelectorAll('[data-gallery-slide]');
      const thumbs = gallery.querySelectorAll('[data-gallery-thumb]');

      let matched = false;
      slides.forEach(s => {
        const active = s.dataset.mediaId === mediaIdStr;
        s.classList.toggle('is-active', active);
        if (active) matched = true;
      });

      if (matched) {
        thumbs.forEach(t => {
          const active = t.dataset.mediaId === mediaIdStr;
          t.classList.toggle('is-active', active);
          t.setAttribute('aria-current', String(active));
        });
      }
    }

    // ── Option change handler ──────────────────────────────────────────
    form.querySelectorAll('[data-variant-option]').forEach(radio => {
      radio.addEventListener('change', () => {
        const variant = findVariantFromSelections();
        applyVariant(variant);
        syncGalleryToVariant(variant);

        // Update selected visual state on option buttons
        radio.closest('[data-option-selector]')
          ?.querySelectorAll('[data-variant-option]')
          .forEach(r => r.closest('label')?.classList.toggle('is-selected', r.checked));
      });
    });

    // ── Quantity stepper ───────────────────────────────────────────────
    if (qtyDecreaseEl && qtyInput) {
      qtyDecreaseEl.addEventListener('click', () => {
        const val = parseInt(qtyInput.value, 10) || 1;
        qtyInput.value = Math.max(1, val - 1);
      });
    }
    if (qtyIncreaseEl && qtyInput) {
      qtyIncreaseEl.addEventListener('click', () => {
        const val = parseInt(qtyInput.value, 10) || 1;
        qtyInput.value = val + 1;
      });
    }
    if (qtyInput) {
      qtyInput.addEventListener('change', () => {
        const val = parseInt(qtyInput.value, 10);
        if (isNaN(val) || val < 1) qtyInput.value = 1;
      });
    }

    // ── Form submit (AJAX add to cart) ─────────────────────────────────
    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      if (!currentVariant?.available) return;

      clearError();
      setLoading(true);

      const quantity = parseInt(qtyInput?.value || '1', 10);
      const payload  = { id: currentVariant.id, quantity };

      try {
        const res = await fetch('/cart/add.js', {
          method:  'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body:    JSON.stringify(payload),
        });

        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.description || `Cart error: ${res.status}`);
        }

        const lineItem = await res.json();

        // ── GA4: add_to_cart event ────────────────────────────────────────
        window.Studio1of1?.track('add_to_cart', {
          currency: window.Studio1of1?.shop?.currency || 'USD',
          value:    (lineItem.final_price * quantity) / 100,
          items: [{
            item_id:       String(lineItem.id),
            item_name:     lineItem.product_title || lineItem.title,
            item_variant:  lineItem.variant_title || undefined,
            price:         lineItem.final_price / 100,
            quantity:      quantity,
          }],
        });

        // Notify cart module and open drawer
        document.dispatchEvent(new CustomEvent('studio1of1:cart:item-added', {
          detail: { lineItem },
          bubbles: true,
        }));

        if (window.Studio1of1?.cart) {
          await window.Studio1of1.cart.refresh();
          window.Studio1of1.cart.open();
        }

        // Reset ATC button text briefly to confirm
        if (atcText) {
          atcText.textContent = window.Studio1of1?.i18n?.added_to_cart || 'Added to cart';
          setTimeout(() => {
            updateButton(currentVariant);
          }, 2000);
        }

      } catch (err) {
        showError(err.message || 'There was a problem adding this item. Please try again.');
        console.error('[product-form.js] Add to cart failed:', err);
      } finally {
        setLoading(false);
      }
    });

    // ── Gallery thumbnail interaction ──────────────────────────────────
    const gallery = document.querySelector('[data-gallery]');
    if (gallery) {
      const slides = gallery.querySelectorAll('[data-gallery-slide]');
      const thumbs = gallery.querySelectorAll('[data-gallery-thumb]');

      thumbs.forEach(thumb => {
        thumb.addEventListener('click', () => {
          const mediaId = thumb.dataset.mediaId;

          slides.forEach(s => s.classList.toggle('is-active', s.dataset.mediaId === mediaId));
          thumbs.forEach(t => {
            const active = t.dataset.mediaId === mediaId;
            t.classList.toggle('is-active', active);
            t.setAttribute('aria-current', String(active));
          });
        });
      });
    }

    // ── Initialise with current variant ───────────────────────────────
    applyVariant(findVariantFromSelections() || variants[0] || null);
  }

})();
